import AudioPlayer, { AudioPlayerProvider } from "@/components/AudioPlayer";
import CallLogs from "@/components/CallLogs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import CallTranscript from "@/components/CallTranscript";
import { toast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import {
  CallResponse,
  PostCallWorkflowOutputs,
  TranslationRequest,
} from "@/lib/types";
import { useAuthStore } from "@/stores/authStore";
import { format } from "date-fns";
import {
  Copy,
  ChevronDown,
  Languages,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Loader2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

// Define new interfaces to match the updated log structure
interface TTFBLogEntry {
  date_time: string;
  service_name: string;
  ttfb: number;
}

interface EventLogEntry {
  date_time: string;
  event: string;
}

interface NewCallLog {
  ttfb: TTFBLogEntry[];
  events: EventLogEntry[];
}

function CallPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationState = location.state as {
    callIds?: string[];
    currentIndex?: number;
    filters?: any;
    totalCalls?: number;
    returnUrl?: string;
  } | null;
  const [call, setCall] = useState<CallResponse | null>(null);
  const [callLogs, setCallLogs] = useState<NewCallLog | null>(null);
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null);
  const [transcript, setTranscript] = useState<string>("");
  const [workflowOutputs, setWorkflowOutputs] =
    useState<PostCallWorkflowOutputs | null>(null);
  const [loadingRecording, setLoadingRecording] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [loadingWorkflowOutputs, setLoadingWorkflowOutputs] = useState(false);
  const [isCallInfoOpen, setIsCallInfoOpen] = useState(true);
  const [isCallLogsOpen, setIsCallLogsOpen] = useState(false);
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
  const [translatedTranscript, setTranslatedTranscript] = useState<string>("");
  const [showTranslation, setShowTranslation] = useState(false);
  const [loadingTranslation, setLoadingTranslation] = useState(false);
  const [navigationLoading, setNavigationLoading] = useState(false);
  const { user } = useAuthStore();

  useEffect(() => {
    if (id) {
      setNavigationLoading(true);
      Promise.all([
        fetchCallDetails(id),
        fetchCallRecording(id),
        user?.is_superuser ? fetchCallLogs(id) : Promise.resolve(),
        fetchCallTranscript(id),
        fetchWorkflowOutputs(id),
      ]).finally(() => {
        setNavigationLoading(false);
      });
    }
  }, [id, user]);

  const fetchCallTranscript = async (callId: string) => {
    try {
      setLoadingTranscript(true);
      const transcriptData = await api.getCallTranscript(callId);
      setTranscript(transcriptData);
    } catch (error) {
      console.error("Failed to fetch call transcript:", error);
      toast({
        title: "Error",
        description: "Failed to fetch call transcript. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingTranscript(false);
    }
  };

  const fetchWorkflowOutputs = async (callId: string) => {
    try {
      setLoadingWorkflowOutputs(true);
      const outputs = await api.getPostCallWorkflowOutputs(callId);
      setWorkflowOutputs(outputs);
    } catch (error) {
      console.error("Failed to fetch workflow outputs:", error);
      toast({
        title: "Error",
        description: "Failed to fetch workflow outputs. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingWorkflowOutputs(false);
    }
  };

  const fetchCallLogs = async (callId: string) => {
    try {
      setLoadingLogs(true);
      const logs = await api.getCallLog(callId);
      setCallLogs(logs.log_lines as unknown as NewCallLog);
    } catch (error) {
      console.error("Failed to fetch call logs:", error);
      toast({
        title: "Error",
        description: "Failed to fetch call logs. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchCallDetails = async (callId: string) => {
    try {
      const details = await api.getCall(callId);
      setCall(details);
    } catch (error) {
      console.error("Failed to fetch call details:", error);
      toast({
        title: "Error",
        description: "Failed to fetch call details. Please try again.",
        variant: "destructive",
      });
    }
  };

  const fetchCallRecording = async (callId: string) => {
    try {
      setLoadingRecording(true);
      const recording = await api.getCallRecording(callId);
      setRecordingBlob(recording);
    } catch (error) {
      console.error("Failed to fetch call recording:", error);
      toast({
        title: "Error",
        description: "Failed to fetch call recording. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingRecording(false);
    }
  };

  const copyCallId = async () => {
    if (call?.id) {
      await navigator.clipboard.writeText(call.id);
      toast({
        title: "Success",
        description: "Call ID copied to clipboard",
      });
    }
  };

  if (!call && !navigationLoading) {
    return <div>Loading...</div>;
  }

  const hasPrevious =
    navigationState?.currentIndex !== undefined &&
    navigationState.currentIndex > 0;
  const hasNext =
    navigationState?.currentIndex !== undefined &&
    navigationState.callIds &&
    navigationState.currentIndex < navigationState.callIds.length - 1;

  const handlePreviousCall = () => {
    if (
      navigationState?.callIds &&
      navigationState.currentIndex !== undefined &&
      hasPrevious
    ) {
      const previousCallId =
        navigationState.callIds[navigationState.currentIndex - 1];
      navigate(`/calls/${previousCallId}`, {
        state: {
          ...navigationState,
          currentIndex: navigationState.currentIndex - 1,
        },
      });
    }
  };

  const handleNextCall = () => {
    if (
      navigationState?.callIds &&
      navigationState.currentIndex !== undefined &&
      hasNext
    ) {
      const nextCallId =
        navigationState.callIds[navigationState.currentIndex + 1];
      navigate(`/calls/${nextCallId}`, {
        state: {
          ...navigationState,
          currentIndex: navigationState.currentIndex + 1,
        },
      });
    }
  };

  return (
    <div className="container py-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => {
              if (navigationState?.returnUrl) {
                navigate(navigationState.returnUrl);
              } else {
                navigate("/calls");
              }
            }}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Calls
          </Button>
          <h1 className="text-3xl font-bold">Call Details</h1>
        </div>
        {navigationState?.callIds && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Call {(navigationState.currentIndex || 0) + 1} of{" "}
              {navigationState.callIds.length}
              {navigationState.totalCalls &&
                navigationState.totalCalls > navigationState.callIds.length &&
                ` (${navigationState.totalCalls} total)`}
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={handlePreviousCall}
              disabled={!hasPrevious}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleNextCall}
              disabled={!hasNext}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      {navigationLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">Loading call details...</p>
          </div>
        </div>
      ) : call ? (
        <>
          <div className="m-8 flex flex-col gap-4">
            <Card className="w-full mt-4">
              <CardHeader>
                <CardTitle>Call Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <strong>Call ID:</strong> {call.id}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={copyCallId}
                      className="h-8 w-8"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <div>
                    <strong>Callee Phone Number:</strong>{" "}
                    {call.callee_phone_number}
                  </div>
                  <div>
                    <strong>Status:</strong> {call.call_status}
                  </div>
                  <div>
                    <strong>Strategy Name:</strong> {call.strategy_name}
                  </div>
                  <div>
                    <strong>Started At:</strong>{" "}
                    {call.call_started_at
                      ? format(
                          new Date(call.call_started_at),
                          "yyyy-MM-dd HH:mm:ss"
                        )
                      : "-"}
                  </div>
                  <div>
                    <strong>Strategy Version:</strong>{" "}
                    {call.strategy_version.version_description}
                  </div>
                  <div>
                    <strong>Ended At:</strong>{" "}
                    {call.call_ended_at
                      ? format(
                          new Date(call.call_ended_at),
                          "yyyy-MM-dd HH:mm:ss"
                        )
                      : "-"}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Call Specific Info Card */}
            {call.call_specific_info &&
              Object.keys(call.call_specific_info).length > 0 && (
                <Collapsible
                  open={isCallInfoOpen}
                  onOpenChange={setIsCallInfoOpen}
                  className="w-full mt-4"
                >
                  <Card className="w-full">
                    <CollapsibleTrigger className="w-full">
                      <CardHeader className="hover:bg-gray-50 cursor-pointer">
                        <CardTitle className="flex items-center justify-between">
                          <span>Call Specific Information</span>
                          <ChevronDown
                            className={`h-4 w-4 transition-transform duration-200 ${
                              isCallInfoOpen ? "transform rotate-180" : ""
                            }`}
                          />
                        </CardTitle>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent>
                        <div className="space-y-3">
                          {Object.entries(call.call_specific_info).map(
                            ([key, value]) => (
                              <div
                                key={key}
                                className="border-b border-gray-100 pb-2 last:border-b-0"
                              >
                                <div className="font-semibold text-sm text-gray-600 mb-1 capitalize">
                                  {key.replace(/_/g, " ")}
                                </div>
                                <div className="text-sm">
                                  {typeof value === "string" ? (
                                    <div className="prose prose-sm max-w-none">
                                      <ReactMarkdown>{value}</ReactMarkdown>
                                    </div>
                                  ) : (
                                    <pre className="whitespace-pre-wrap font-mono text-xs bg-gray-50 p-2 rounded">
                                      {JSON.stringify(value, null, 2)}
                                    </pre>
                                  )}
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              )}

            {loadingRecording && (
              <div className="text-gray-600 font-bold mt-6">
                Loading recording...
              </div>
            )}

            {recordingBlob && (
              <Card className="w-full mt-4">
                <CardHeader>
                  <CardTitle>Call Recording</CardTitle>
                </CardHeader>
                <CardContent>
                  <AudioPlayerProvider>
                    <AudioPlayer
                      id={call.id}
                      audioBlob={recordingBlob}
                      darkMode={false}
                      height={100}
                      layoutType="row"
                    />
                  </AudioPlayerProvider>
                  <div className="mt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        const url = URL.createObjectURL(recordingBlob);
                        const a = document.createElement('a');
                        a.href = url;

                        // Determine file extension from MIME type
                        const mimeType = recordingBlob.type;
                        let extension = 'wav'; // default
                        if (mimeType.includes('mp3')) extension = 'mp3';
                        else if (mimeType.includes('mp4')) extension = 'mp4';
                        else if (mimeType.includes('m4a')) extension = 'm4a';
                        else if (mimeType.includes('ogg')) extension = 'ogg';
                        else if (mimeType.includes('webm')) extension = 'webm';

                        a.download = `call-${call.id}-recording.${extension}`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      }}
                    >
                      Download Recording
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {user?.is_superuser && (
              <Collapsible
                open={isCallLogsOpen}
                onOpenChange={setIsCallLogsOpen}
                className="w-full mt-4"
              >
                <Card className="w-full">
                  <CollapsibleTrigger className="w-full">
                    <CardHeader className="hover:bg-gray-50 cursor-pointer">
                      <CardTitle className="flex items-center justify-between">
                        <span>Call Logs</span>
                        <ChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${
                            isCallLogsOpen ? "transform rotate-180" : ""
                          }`}
                        />
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent>
                      <CallLogs callLogs={callLogs} loading={loadingLogs} />
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

            {/* Transcript Card */}
            <Collapsible
              open={isTranscriptOpen}
              onOpenChange={setIsTranscriptOpen}
              className="w-full mt-4"
            >
              <Card className="w-full">
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="hover:bg-gray-50 cursor-pointer">
                    <CardTitle className="flex items-center justify-between">
                      <span>Call Transcript</span>
                      <div className="flex items-center gap-2">
                        {transcript && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (showTranslation) {
                                setShowTranslation(false);
                              } else {
                                if (!translatedTranscript) {
                                  try {
                                    setLoadingTranslation(true);
                                    const translationRequest: TranslationRequest =
                                      {
                                        text: transcript,
                                        target_language: "english",
                                      };
                                    const response = await api.translateText(
                                      translationRequest
                                    );
                                    setTranslatedTranscript(
                                      response.translated_text
                                    );
                                  } catch (error) {
                                    console.error(
                                      "Failed to translate transcript:",
                                      error
                                    );
                                    toast({
                                      title: "Error",
                                      description:
                                        "Failed to translate transcript. Please try again.",
                                      variant: "destructive",
                                    });
                                    return;
                                  } finally {
                                    setLoadingTranslation(false);
                                  }
                                }
                                setShowTranslation(true);
                              }
                            }}
                            disabled={loadingTranslation}
                          >
                            <Languages className="h-4 w-4 mr-1" />
                            {loadingTranslation
                              ? "Translating..."
                              : showTranslation
                              ? "Show Original"
                              : "Translate"}
                          </Button>
                        )}
                        <ChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${
                            isTranscriptOpen ? "transform rotate-180" : ""
                          }`}
                        />
                      </div>
                    </CardTitle>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent>
                    {loadingTranscript ? (
                      <div className="text-gray-600 font-bold">
                        Loading transcript...
                      </div>
                    ) : transcript ? (
                      <ScrollArea className="h-[400px] w-full rounded-md border">
                        {showTranslation ? (
                          <CallTranscript
                            transcriptText={translatedTranscript}
                          />
                        ) : (
                          <CallTranscript transcriptText={transcript} />
                        )}
                      </ScrollArea>
                    ) : (
                      <div className="text-gray-600">
                        No transcript available
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Workflow Outputs Card */}
            <Card className="w-full mt-4">
              <CardHeader>
                <CardTitle>Post-Call Workflow Outputs</CardTitle>
              </CardHeader>
              <CardContent>
                {loadingWorkflowOutputs ? (
                  <div className="text-gray-600 font-bold">
                    Loading workflow outputs...
                  </div>
                ) : workflowOutputs &&
                  Object.keys(workflowOutputs).length > 0 ? (
                  <ScrollArea className="h-[400px] w-full rounded-md border">
                    <div className="p-4">
                      <pre className="whitespace-pre-wrap">
                        {JSON.stringify(workflowOutputs, null, 2)}
                      </pre>
                    </div>
                    <div className="absolute top-4 right-4">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          navigator.clipboard.writeText(
                            JSON.stringify(workflowOutputs, null, 2)
                          );
                          toast({
                            title: "Success",
                            description: "Workflow outputs copied to clipboard",
                          });
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-gray-600">
                    No workflow outputs available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      ) : (
        <div>Loading...</div>
      )}
    </div>
  );
}

export default CallPage;
